<template>
  <div class="doors">

    <header class="masthead">
      <h1 class="title">Doors<span class="dot">.</span></h1>
    </header>

    <!-- Add or edit -->
    <div class="form">
      <label class="field">
        <span class="field-label">Door ID</span>
        <input
          v-model="form.doorUuid"
          class="input"
          type="text"
          placeholder="door-front-001"
          :disabled="editing"
        >
      </label>

      <label class="field">
        <span class="field-label">Office</span>
        <select v-model="form.office" class="input">
          <option value="">Select an office</option>
          <option v-for="o in offices" :key="o.key" :value="o.key">
            {{ o.name }} ({{ o.code }})
          </option>
        </select>
      </label>

      <label class="field grow">
        <span class="field-label">Label <span class="optional">optional</span></span>
        <input
          v-model="form.doorLabel"
          class="input"
          type="text"
          placeholder="Front entrance"
        >
      </label>

      <button class="btn primary" :disabled="!canSave || saving" @click="save">
        {{ saving ? 'Saving…' : (editing ? 'Update' : 'Add door') }}
      </button>

      <button v-if="editing" class="btn" @click="reset">Cancel</button>
    </div>

    <p v-if="formError" class="msg err">{{ formError }}</p>

    <p v-if="loading" class="msg">Reading doors</p>

    <p v-else-if="error" class="msg err">
      {{ error }}
      <button class="link" @click="load">Try again</button>
    </p>

    <p v-else-if="doors.length === 0" class="msg">
      No doors mapped yet. Badge events from an unmapped door are rejected.
    </p>

    <table v-else class="table">
      <thead>
        <tr>
          <th>Door ID</th>
          <th>Label</th>
          <th>Office</th>
          <th></th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="door in doors" :key="door.door_uuid">
          <td class="mono">{{ door.door_uuid }}</td>
          <td>{{ door.door_label || '\u2014' }}</td>
          <td>
            <span class="office">
              <span class="code">{{ door.country_code }}</span>
              {{ door.office_name }}
              <!-- An office that no longer exists in Entra is worth flagging:
                   the mapping still resolves, but nobody will ever be shown at
                   it, so a rename has silently broken the link. -->
              <span v-if="!officeExists(door)" class="orphan">not in Entra</span>
            </span>
          </td>
          <td class="actions">
            <button class="link" @click="edit(door)">Edit</button>
            <button class="link danger" @click="remove(door)">Delete</button>
          </td>
        </tr>
      </tbody>
    </table>

  </div>
</template>


<script setup>
import { ref, computed, onMounted } from 'vue'
import { getLocations } from '@/services/OfficeColourService'
import { getDoors, setDoor, deleteDoor } from '@/services/DoorService'

const doors = ref([])
const offices = ref([])
const loading = ref(true)
const error = ref(null)
const saving = ref(false)
const formError = ref(null)
const editing = ref(false)

const form = ref({ doorUuid: '', office: '', doorLabel: '' })

const canSave = computed(() =>
  form.value.doorUuid.trim().length > 0 && form.value.office.length > 0
)

function officeExists(door) {
  return offices.value.some(
    (o) => o.code === door.country_code && o.name === door.office_name
  )
}

async function load() {
  loading.value = true
  error.value = null

  try {
    // Locations come from Vero now, not from Entra's officeLocation field.
    // That field is largely blank in a real directory, which left this dropdown
    // empty and doors with nothing to point at.
    const [locationList, doorList] = await Promise.all([getLocations(), getDoors()])

    offices.value = (locationList ?? []).map((l) => ({
      code: l.country,
      name: l.name,
      // Country and name packed into one value, since a select carries one
      // value and the pair is what identifies a location.
      key: `${l.country}|${l.name}`
    }))

    doors.value = doorList
  } catch (err) {
    console.error(err)
    error.value = err.message ?? String(err)
  } finally {
    loading.value = false
  }
}

function reset() {
  form.value = { doorUuid: '', office: '', doorLabel: '' }
  editing.value = false
  formError.value = null
}

function edit(door) {
  form.value = {
    doorUuid: door.door_uuid,
    office: `${door.country_code}|${door.office_name}`,
    doorLabel: door.door_label || ''
  }
  editing.value = true
  formError.value = null
}

async function save() {
  saving.value = true
  formError.value = null

  const [countryCode, officeName] = form.value.office.split('|')

  try {
    await setDoor({
      doorUuid: form.value.doorUuid.trim(),
      countryCode,
      officeName,
      doorLabel: form.value.doorLabel.trim()
    })
    reset()
    await load()
  } catch (err) {
    console.error(err)
    formError.value = err.message ?? String(err)
  } finally {
    saving.value = false
  }
}

async function remove(door) {
  formError.value = null

  try {
    await deleteDoor(door.door_uuid)
    await load()
  } catch (err) {
    console.error(err)
    formError.value = err.message ?? String(err)
  }
}

onMounted(load)
</script>


<style scoped>
.doors {
  --ink: #003c6c;
  --ink-soft: #64748b;
  --accent: #ed6e01;
  --line: #dfe4e9;
  --wash: #f7f9fb;
  --paper: #ffffff;

  /* Left aligned, like every other page. Centring made the content drift right
     of the menu and the header logo, so nothing lined up with anything else. */
  max-width: 1100px;
  margin: 0;
  padding: 0 24px 80px;
  text-align: left;
  color: #191c1e;
}

.masthead {
  padding: 30px 0 22px;
  border-bottom: 1px solid var(--line);
  margin-bottom: 26px;
}

.title {
  font-size: clamp(30px, 4.2vw, 44px);
  font-weight: 700;
  letter-spacing: -0.03em;
  margin: 0;
  color: var(--ink);
}

.dot { color: var(--accent); }

/* ---------- Form ---------- */

.form {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 14px;
  margin-bottom: 22px;
}

.field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.field.grow { flex: 1; min-width: 180px; }

.field-label {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.09em;
  text-transform: uppercase;
  color: var(--ink-soft);
}

.optional {
  font-weight: 400;
  letter-spacing: 0.02em;
  text-transform: none;
  color: #9aa5b2;
}

.input {
  font-family: inherit;
  font-size: 15px;
  height: 46px;
  min-width: 220px;
  padding: 0 14px;
  border: 1px solid var(--line);
  border-radius: 10px;
  background: var(--paper);
  color: #191c1e;
  outline: none;
}

.input:focus { border-color: var(--ink); }
.input:disabled { background: var(--wash); color: var(--ink-soft); }

.btn {
  font-family: inherit;
  font-size: 14px;
  font-weight: 600;
  height: 46px;
  padding: 0 22px;
  border: 1px solid var(--ink);
  border-radius: 10px;
  background: transparent;
  color: var(--ink);
  cursor: pointer;
}

.btn:hover:not(:disabled) { background: var(--ink); color: var(--paper); }
.btn:disabled { opacity: 0.4; cursor: default; }

.btn.primary { background: var(--ink); color: var(--paper); }
.btn.primary:hover:not(:disabled) { background: #00548f; }

/* ---------- Table ---------- */

.table {
  width: 100%;
  border-collapse: collapse;
  font-size: 15px;
  border: 1px solid var(--line);
  border-radius: 12px;
  overflow: hidden;
}

.table th {
  text-align: left;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.09em;
  text-transform: uppercase;
  color: var(--ink-soft);
  padding: 13px 18px;
  border-bottom: 1px solid var(--line);
  background: var(--wash);
}

.table td {
  padding: 13px 18px;
  border-bottom: 1px solid #f1f4f7;
}

.table tbody tr:last-child td { border-bottom: none; }

.mono {
  font-family: ui-monospace, "SF Mono", Menlo, monospace;
  font-size: 13.5px;
}

.office {
  display: inline-flex;
  align-items: center;
  gap: 9px;
}

.code {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.06em;
  background: var(--ink);
  color: var(--paper);
  padding: 3px 7px;
  border-radius: 4px;
}

.orphan {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  /* Grey. This marks a door pointing at a location that no longer exists,
     which does need fixing, but the label already says so. */
  color: #64748b;
  background: #f1f4f7;
  padding: 3px 8px;
  border-radius: 4px;
}

.actions {
  text-align: right;
  white-space: nowrap;
}

/* ---------- Messages ---------- */

.msg {
  font-size: 16px;
  color: var(--ink-soft);
  padding: 8px 0;
  margin: 0;
}

.msg.err { color: #b3261e; }

.link {
  font-family: inherit;
  font-size: 14px;
  color: var(--ink);
  background: none;
  border: none;
  padding: 0;
  margin-left: 12px;
  text-decoration: underline;
  cursor: pointer;
}

.link.danger { color: #b3261e; }

@media (max-width: 760px) {
  .doors { padding: 0 20px 60px; }
  .form { flex-direction: column; align-items: stretch; }
  .input { min-width: 0; width: 100%; }
}
</style>
